import React from 'react';
import { Box } from '@mui/material';

type WhiteNoteName = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G';

type KeyboardRowConfig = {
  width: string;
  maxWidth: string;
  whiteKeys: number;
  startNote: WhiteNoteName;
  highlightIndex: number;
  keyboardHeight: number;
  labelFontSize: string;
};

const whiteNoteCycle: WhiteNoteName[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const hasBlackKeyAfter = new Set<WhiteNoteName>(['C', 'D', 'F', 'G', 'A']);

const keyboardRows: KeyboardRowConfig[] = [
  {
    width: '96%',
    maxWidth: '940px',
    whiteKeys: 38,
    startNote: 'A',
    highlightIndex: 16,
    keyboardHeight: 176,
    labelFontSize: 'clamp(10px, 0.95vw, 16px)',
  },
  {
    width: '67%',
    maxWidth: '590px',
    whiteKeys: 29,
    startNote: 'C',
    highlightIndex: 14,
    keyboardHeight: 146,
    labelFontSize: 'clamp(11px, 1vw, 17px)',
  },
  {
    width: '41%',
    maxWidth: '372px',
    whiteKeys: 15,
    startNote: 'C',
    highlightIndex: 7,
    keyboardHeight: 122,
    labelFontSize: 'clamp(12px, 1.05vw, 18px)',
  },
];

const buildWhiteKeyLabels = (startNote: WhiteNoteName, whiteKeys: number) => {
  const startIndex = whiteNoteCycle.indexOf(startNote);
  return Array.from({ length: whiteKeys }, (_, index) => {
    return whiteNoteCycle[(startIndex + index) % whiteNoteCycle.length];
  });
};

const KeyboardReferenceRow: React.FC<KeyboardRowConfig> = ({
  width,
  maxWidth,
  whiteKeys,
  startNote,
  highlightIndex,
  keyboardHeight,
  labelFontSize,
}) => {
  const labels = buildWhiteKeyLabels(startNote, whiteKeys);
  const keybedInset = {
    xs: '34px 16px 10px',
    md: '38px 18px 12px',
  };

  return (
    <Box
      sx={{
        width,
        maxWidth,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: `${keyboardHeight}px`,
          padding: {
            xs: '12px 16px 10px',
            md: '14px 18px 12px',
          },
          borderRadius: '6px',
          background: 'linear-gradient(180deg, #1c1d21 0%, #07080b 15%, #0b0c10 54%, #111218 100%)',
          boxShadow: '0 14px 28px rgba(0, 0, 0, 0.36)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '6px',
            left: '18px',
            right: '18px',
            height: '30%',
            borderRadius: '999px',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.03) 100%)',
            filter: 'blur(1px)',
            opacity: 0.48,
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '22px',
            background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.22) 100%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: keybedInset,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${whiteKeys}, minmax(0, 1fr))`,
              alignItems: 'end',
            }}
          >
            {labels.map((label, index) => (
              <Box
                key={`white-${label}-${index}`}
                sx={{
                  position: 'relative',
                  height: '100%',
                  borderLeft: index === 0 ? '1px solid rgba(92, 92, 92, 0.32)' : 'none',
                  borderRight: '1px solid rgba(111, 111, 111, 0.42)',
                  borderBottom: '1px solid rgba(150, 150, 150, 0.18)',
                  background: 'linear-gradient(180deg, #fefefe 0%, #f5f3f3 58%, #ebe9ea 100%)',
                  boxShadow:
                    'inset 0 -10px 14px rgba(204, 204, 204, 0.48), inset 0 1px 0 rgba(255,255,255,0.75)',
                }}
              >
                {index === highlightIndex && (
                  <>
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                          'linear-gradient(180deg, rgba(187, 199, 255, 0.28) 0%, rgba(157, 176, 255, 0.72) 100%)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: '0 auto 0 0',
                        width: '100%',
                        background: 'rgba(150, 166, 255, 0.32)',
                        mixBlendMode: 'multiply',
                      }}
                    />
                  </>
                )}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {labels.map((label, index) => {
              if (!hasBlackKeyAfter.has(label) || index >= labels.length - 1) return null;

              return (
                <Box
                  key={`black-${label}-${index}`}
                  sx={{
                    position: 'absolute',
                    top: '-1px',
                    left: `${((index + 1) / whiteKeys) * 100}%`,
                    transform: 'translateX(-50%)',
                    width: `calc((100% / ${whiteKeys}) * 0.6)`,
                    height: '58%',
                    borderRadius: '0 0 4px 4px',
                    background:
                      'linear-gradient(180deg, #040507 0%, #1b1d24 8%, #07080b 56%, #16181e 100%)',
                    boxShadow:
                      '0 8px 12px rgba(0,0,0,0.34), inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -3px 8px rgba(255,255,255,0.04)',
                    zIndex: 3,
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(${whiteKeys}, minmax(0, 1fr))`,
          color: '#0E1117',
          fontWeight: 700,
          letterSpacing: '0.02em',
          lineHeight: 1,
          fontSize: labelFontSize,
          userSelect: 'none',
        }}
      >
        {labels.map((label, index) => (
          <Box
            key={`label-${label}-${index}`}
            sx={{
              textAlign: 'center',
              color: index === highlightIndex ? '#7B91FF' : '#0E1117',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const MiddleCReference: React.FC = () => {
  return (
    <Box
      sx={{
        borderRadius: '18px',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 100%)',
        border: '1px solid rgba(121, 216, 255, 0.18)',
        padding: { xs: '14px', md: '18px' },
        marginBottom: '16px',
      }}
    >
      <Box
        sx={{
          color: '#F5F9FF',
          fontSize: '14px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          marginBottom: '14px',
        }}
      >
        Middle C Reference
      </Box>

      <Box
        sx={{
          borderRadius: '16px',
          background: 'linear-gradient(180deg, #e6e6e6 0%, #d9d9d9 100%)',
          padding: { xs: '16px 12px', md: '22px 18px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: '24px', md: '30px' },
        }}
      >
        {keyboardRows.map((row) => (
          <KeyboardReferenceRow key={`${row.startNote}-${row.whiteKeys}`} {...row} />
        ))}
      </Box>
    </Box>
  );
};

export default MiddleCReference;
